import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  message,
  Drawer,
  Descriptions,
  Popconfirm,
  Statistic,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { customers, payments as paymentsApi } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtCurrency, fmtDate } from "../utils/format";

export default function Customers() {
  const navigate = useNavigate();
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payments, setPayments] = useState([]);
  const [holdingItems, setHoldingItems] = useState([]);
  const [totalAdvance, setTotalAdvance] = useState(0);
  const [unpaidSummary, setUnpaidSummary] = useState({ count: 0, totalAdvance: 0, totalUnpaid: 0 });
  const [q, setQ] = useState("");
  const [filterOutstanding, setFilterOutstanding] = useState(null);
  const [page, setPage] = useState(0);
  const [form] = Form.useForm();

  const load = async (pg = page, search = q, outstanding = filterOutstanding) => {
    setLoading(true);
    try {
      const r = await customers.list({ q: search, page: pg, size: 20,
        hasOutstanding: outstanding === "yes" ? true : outstanding === "no" ? false : undefined });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);

  const openDetail = async (rec) => {
    // FIX: always re-fetch the customer by id so `outstanding` is fresh
    // (the list response caches the value from when the page loaded,
    //  but it changes every time a rental return is processed).
    try {
      const fresh = await customers.get(rec.id);
      setDetail(fresh.data.data);
    } catch {
      setDetail(rec); // fall back to row data
    }
    const [paymentsRes, holdingRes, advanceRes, unpaidRes] = await Promise.all([
      customers.payments(rec.id),
      customers.holdingItems(rec.id),
      customers.totalAdvance(rec.id),
      customers.unpaidSummary(rec.id),
    ]);
    setPayments(paymentsRes.data.data);
    setHoldingItems(holdingRes.data.data || []);
    setTotalAdvance(Number(advanceRes.data.data || 0));
    setUnpaidSummary(unpaidRes.data.data || { count: 0, totalAdvance: 0, totalUnpaid: 0 });
  };

  const onDelete = async (id) => {
    try {
      await customers.delete(id);
      message.success("Customer deleted");
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || "Cannot delete customer");
    }
  };

  const onSave = async (vals) => {
    try {
      if (editing?.id) await customers.update(editing.id, vals);
      else await customers.create(vals);
      message.success("Saved");
      setModal(false);
      load();
    } catch {
      message.error("Error");
    }
  };

  const cols = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v) => <b className="mono">{v}</b>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (v) => <span className="mono">{v}</span>,
    },
    {
      title: "GST",
      dataIndex: "gstNumber",
      render: (v) => v || <span className="text-muted">-</span>,
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      // FIX: show red if > 0 (money owed), green if 0 or cleared
      render: (v) => (
        <span
          className="mono"
          style={{
            color: Number(v) > 0 ? "var(--danger)" : "var(--success)",
            fontWeight: 600,
          }}
        >
          {fmtCurrency(v)}
        </span>
      ),
    },
    {
      title: "Unsettled Bills",
      dataIndex: "unsettledBillsCount",
      render: (v) =>
        Number(v) > 0 ? (
          <span className="mono" style={{ color: "var(--danger)", fontWeight: 600 }}>{v}</span>
        ) : (
          <span className="text-muted">0</span>
        ),
    },
    {
      title: "Advance (those bills)",
      dataIndex: "unsettledAdvance",
      render: (v) =>
        Number(v) > 0 ? (
          <span className="mono" style={{ color: "#d46b08" }}>{fmtCurrency(v)}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Total Unsettled",
      dataIndex: "unsettledAmount",
      render: (v) =>
        Number(v) > 0 ? (
          <span className="mono" style={{ color: "var(--danger)", fontWeight: 600 }}>{fmtCurrency(v)}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Actions",
      key: "a",
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(r);
              form.setFieldsValue(r);
              setModal(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDetail(r)}
          >
            View
          </Button>
          <Popconfirm
            title="Delete this customer?"
            description="This cannot be undone."
            onConfirm={() => onDelete(r.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Manage customer accounts"
        actions={[
          <Button
            key="a"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              form.resetFields();
              setModal(true);
            }}
          >
            Add Customer
          </Button>,
        ]}
      />

      {/* ── Filters ────────────────────────────── */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search name or phone..."
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          placeholder="Outstanding"
          allowClear
          value={filterOutstanding}
          onChange={(v) => { setFilterOutstanding(v); setPage(0); load(0, q, v); }}
          style={{ width: 160 }}
          options={[
            { value: "yes", label: "Has Outstanding" },
            { value: "no",  label: "No Outstanding" },
          ]}
        />
      </Space>

      <Table
        columns={cols}
        dataSource={data.content}
        scroll={{ x: "max-content" }}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      <Modal
        open={modal}
        title={editing?.id ? "Edit Customer" : "New Customer"}
        onCancel={() => setModal(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item
            name="name"
            label="Customer Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="gstNumber" label="GST Number">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Save
          </Button>
        </Form>
      </Modal>

      <Drawer
        title={`Customer: ${detail?.name}`}
        open={!!detail}
        onClose={() => {
          setDetail(null);
          setHoldingItems([]);
          setTotalAdvance(0);
          setUnpaidSummary({ count: 0, totalAdvance: 0, totalUnpaid: 0 });
        }}
        width={500}
      >
        {detail && (
          <>
            {/* Outstanding + Advance summary */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  flex: 1,
                  background: Number(detail.outstanding) > 0 ? "#fff1f0" : "#f6ffed",
                  border: `1px solid ${Number(detail.outstanding) > 0 ? "#ffa39e" : "#b7eb8f"}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Outstanding Balance</span>
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: "var(--font-mono)",
                    color: Number(detail.outstanding) > 0 ? "var(--danger)" : "var(--success)",
                  }}
                >
                  {fmtCurrency(detail.outstanding)}
                </span>
              </div>
              <div
                style={{
                  flex: 1,
                  background: totalAdvance > 0 ? "#fff7e6" : "#f6ffed",
                  border: `1px solid ${totalAdvance > 0 ? "#ffd591" : "#b7eb8f"}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Total Advance Held</span>
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: "var(--font-mono)",
                    color: totalAdvance > 0 ? "#d46b08" : "var(--success)",
                  }}
                >
                  {fmtCurrency(totalAdvance)}
                </span>
              </div>
            </div>

            {unpaidSummary.count > 0 && (
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <div
                  onClick={() => navigate("/rental-returns", {
                    state: {
                      customerId: detail.id,
                      customerName: detail.name,
                      status: "PENDING,PARTIAL",
                    },
                  })}
                  title="View unsettled rental returns for this customer"
                  style={{
                    flex: 1,
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 8,
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Unpaid Bills</span>
                  <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--danger)" }}>
                    {unpaidSummary.count}
                  </span>
                </div>
                <div
                  style={{
                    flex: 1,
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 8,
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Advance (those bills)</span>
                  <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-mono)", color: "#d46b08" }}>
                    {fmtCurrency(unpaidSummary.totalAdvance)}
                  </span>
                </div>
                <div
                  onClick={() => navigate("/rental-returns", {
                    state: {
                      customerId: detail.id,
                      customerName: detail.name,
                      status: "PENDING,PARTIAL",
                    },
                  })}
                  title="View unsettled rental returns for this customer"
                  style={{
                    flex: 1,
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 8,
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Total Unsettled</span>
                  <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--danger)" }}>
                    {fmtCurrency(unpaidSummary.totalUnpaid)}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              {[
                ["Phone", detail.phone],
                ["Address", detail.address || "-"],
                ["GST", detail.gstNumber || "-"],
              ].map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid var(--border)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: "var(--text-muted)" }}>{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>

            {/* ── Currently Holding Items ── */}
            <h4
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "var(--text-muted)",
                marginBottom: 8,
              }}
            >
              Currently Holding
            </h4>

            <Table
              dataSource={holdingItems}
              rowKey="rentalItemId"
              size="small"
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
              style={{ marginBottom: 20 }}
              columns={[
                { title: "Product", dataIndex: "productName", render: (v) => <strong style={{ fontSize: 12 }}>{v}</strong> },
                { title: "Unit", dataIndex: "unit" },
                { title: "Qty", dataIndex: "qtyHolding" },
                { title: "Rental No", dataIndex: "rentalNo", render: (v) => <span className="mono text-info" style={{ fontSize: 11 }}>{v}</span> },
                {
                  title: "Time Out",
                  render: (_, item) => {
                    const hrs = Math.floor(item.minutesOut / 60);
                    return item.daysOut > 0 ? `${item.daysOut}d ${hrs % 24}h` : `${hrs}h`;
                  },
                },
                { title: "Bill So Far", dataIndex: "billSoFar", render: (v) => <span className="mono text-accent">{fmtCurrency(v)}</span> },
              ]}
            />

            {/* ── Payment History ── */}
            <h4
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "var(--text-muted)",
                marginBottom: 8,
              }}
            >
              Payment History
            </h4>

            <Table
              dataSource={payments}
              rowKey="id"
              size="small"
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
              columns={[
                { title: "No", dataIndex: "paymentNo", render: (v) => <span className="mono text-info" style={{ fontSize: 11 }}>{v}</span> },
                { title: "Date", dataIndex: "paymentDate", render: fmtDate },
                { title: "Type", dataIndex: "refType", render: (v) => v ? <Tag style={{ fontSize: 10 }}>{v}</Tag> : "—" },
                { title: "Mode", dataIndex: "mode", render: (v) => <Tag style={{ fontSize: 10 }}>{v}</Tag> },
                { title: "Amount", dataIndex: "amount", render: (v) => <span className="mono text-success" style={{ fontSize: 11 }}>{fmtCurrency(v)}</span> },
                { title: "Notes", dataIndex: "notes", render: (v) => v || "—" },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}